import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Loader2, Send, Mail, MapPin, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', topic: 'editorial', message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        setSent(true);
        toast.success(t('contact.success'));
      } else {
        toast.error(t('contact.error'));
      }
    } catch { toast.error(t('contact.error')); }
    finally { setSubmitting(false); }
  }

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <Helmet><title>Kontakt — e-mina.eu</title></Helmet>
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="contact-page">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 dark:text-white mb-3">{t('contact.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">{t('contact.subtitle')}</p>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Info block */}
          <aside className="space-y-4 text-sm">
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100 dark:text-white">{t('contact.info_name')}</p>
              <p className="text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-1"><MapPin size={14} /> {t('contact.info_address')}</p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400 flex items-center gap-2"><Mail size={14} /> {t('contact.info_email')}</p>
            </div>
          </aside>

          {/* Form */}
          <div className="md:col-span-2">
            {sent ? (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-8 text-center" data-testid="contact-success">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-800 mx-auto flex items-center justify-center mb-3">
                  <Check className="text-emerald-600 dark:text-emerald-300" size={24} />
                </div>
                <p className="font-semibold text-gray-900 dark:text-gray-100 dark:text-white mb-1">{t('contact.success')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required value={form.first_name} onChange={update('first_name')} placeholder={t('contact.first_name')}
                         className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:border-red-400" data-testid="contact-first-name" />
                  <input required value={form.last_name} onChange={update('last_name')} placeholder={t('contact.last_name')}
                         className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:border-red-400" data-testid="contact-last-name" />
                </div>
                <input required type="email" value={form.email} onChange={update('email')} placeholder={t('contact.email')}
                       className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:border-red-400" data-testid="contact-email" />
                <select value={form.topic} onChange={update('topic')}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:border-red-400" data-testid="contact-topic">
                  <option value="editorial">{t('contact.topic_editorial')}</option>
                  <option value="advertising">{t('contact.topic_advertising')}</option>
                  <option value="collaboration">{t('contact.topic_collaboration')}</option>
                  <option value="other">{t('contact.topic_other')}</option>
                </select>
                <textarea required rows={6} value={form.message} onChange={update('message')} placeholder={t('contact.message')}
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:border-red-400 resize-none" data-testid="contact-message" />
                <button type="submit" disabled={submitting}
                        className="w-full py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2" data-testid="contact-submit">
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} {t('contact.submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
